import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useEffect, useState } from "react";
import { X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle } from
"../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { AlertBanner, ui } from "../../../components/ui";
import { getRegularizationReport, approveRegularization } from "../../../services/attendanceService";
import { formatDate, formatTime } from "../utils/dateUtils";

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="text-sm font-semibold per-file-hint">{label}</span>
      <span className="text-sm text-right text-foreground">
        {value ?? <span className="per-file-hint">—</span>}
      </span>
    </div>);

}

function ComparisonRow({ label, oldValue, requestedValue }) {
  return (
    <div className="grid grid-cols-2 gap-4 py-2.5">
      <div>
        <p className="text-xs font-semibold per-file-hint">Old {label} </p>
        <p className="text-sm font-mono text-foreground">{oldValue ?? "—"}</p>
      </div>
      <div>
        <p className="text-xs font-semibold per-file-hint">Requested {label} </p>
        <p className="text-sm font-mono text-foreground">{requestedValue ?? "—"}</p>
      </div>
    </div>
  );
}

function timesDiffer(a, b) {
  // Compare at minute granularity to match what formatTime actually displays,
  // so seconds/microsecond-only differences (or unparseable dates) don't
  // false-flag a field as "changed" when it looks identical in the UI.
  const da = a ? new Date(a) : null;
  const db = b ? new Date(b) : null;
  const validA = da && !Number.isNaN(da.getTime());
  const validB = db && !Number.isNaN(db.getTime());

  if (!validA && !validB) return false;
  if (validA !== validB) return true;

  // Truncate to the minute so trailing seconds/microseconds don't count
  const na = Math.floor(da.getTime() / 60000);
  const nb = Math.floor(db.getTime() / 60000);
  return na !== nb;
}

function getRegularizationScope(regDetail) {
  const checkInChanged = timesDiffer(regDetail.currentCheckInTime, regDetail.requestedCheckInTime);
  const checkOutChanged = timesDiffer(regDetail.currentCheckOutTime, regDetail.requestedCheckOutTime);
  if (checkInChanged && checkOutChanged) return "Check-In and Check-Out";
  if (checkInChanged) return "Check-In";
  if (checkOutChanged) return "Check-Out";
  return null;
}

export function RegularizationReviewModal({ record, onOpenChange, onDecisionSubmitted }) {
  const [regDetail, setRegDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    setRegDetail(null);
    setRemarks("");
    setSubmitError("");
    if (!record?.attendanceId || !record?.attendanceDate) {
      setLoadError("");
      return;
    }
    setLoading(true);
    setLoadError("");
    getRegularizationReport(record.attendanceId)
    .then((data) => {
      const rows = Array.isArray(data) ? data : data ? [data] : [];
      const match =
      rows.find((r) => r.attendanceId === record.attendanceId) || rows[0] || null;
      if (match) {
        setRegDetail(match);
      } else {
        setLoadError("No regularization request found for this record.");
      }
    })
    .catch((err) => setLoadError(err.message || "Failed to load regularization details."))
    .finally(() => setLoading(false));
  }, [record]);

  const submitDecision = async (status) => {
    if (!regDetail || submitting) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await approveRegularization({
        regularizationId: regDetail.regularizationId,
        attendanceId: regDetail.attendanceId,
        reason: remarks.trim(),
        status
      });
      onDecisionSubmitted?.();
    } catch (err) {
      setSubmitError(err.message || "Failed to submit decision.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!record} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border border-white/60 bg-white shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] font-sans">
          {record &&
          <>
              <div className="flex items-center justify-between px-6 py-4 border-b border-(--color-text-border)/70">
                <div>
                  <DialogTitle className="text-xl font-bold text-foreground">
                    <Trans i18nKey="attendance.reviewRegularization" />
                  </DialogTitle>
                </div>
                <DialogPrimitive.Close className="h-9 w-9 grid place-items-center rounded-xl per-file-hint hover:bg-workspace/60 hover:text-primary transition cursor-pointer">
                  <X className="h-5 w-5" />
                  <span className="sr-only"><Trans i18nKey="formActions.close" /></span>
                </DialogPrimitive.Close>
              </div>

              <div className="px-6 py-2">
                {loading &&
              <p className="py-4 text-sm per-file-hint">
                    <Trans i18nKey="attendance.loadingRegularizationRequest" />
                  </p>
              }
                {!loading && loadError &&
              <p className="py-4 text-sm text-error">{loadError}</p>
              }

                {!loading && regDetail &&
              <>
                    <div className="divide-y divide-(--color-text-border)/40">
                      <Row label="Name" value={record.empName || "—"} />
                      <Row label="Date" value={formatDate(regDetail.attendanceDate ?? record.attendanceDate)} />
                      <Row label="Attendance ID" value={regDetail.attendanceId} />

                    </div>

                    {(() => {
                      const scope = getRegularizationScope(regDetail);
                      return (
                        <>
                          {scope && (
                            <p className="text-sm font-semibold text-primary pt-5 pb-1">
                              Regularize  : {scope}
                            </p>
                          )}

                          <div className="divide-y divide-(--color-text-border)/40">
                            <ComparisonRow
                              label = "Check-In"
                              oldValue={formatTime(regDetail.currentCheckInTime)}
                              requestedValue={formatTime(regDetail.requestedCheckInTime)}

                            />
                            <ComparisonRow
                              label="Check-Out"
                              oldValue={formatTime(regDetail.currentCheckOutTime)}
                              requestedValue={formatTime(regDetail.requestedCheckOutTime)}
                            />
                            <Row label="Employee's Reason" value={regDetail.reason} />
                          </div>
                        </>
                      );
                    })()}

                    <div className="pt-4">
                      <label className="text-sm font-semibold text-foreground">
                        <Trans i18nKey="common.managerRemarks" />
                      </label>
                      <Textarea
                    className="mt-1 rounded-xl"
                    placeholder={i18n.t("formActions.addARemarkForThisDecision")}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)} />

                    </div>

                    {submitError ?
                <div className="pt-3">
                        <AlertBanner type="error">{submitError}</AlertBanner>
                      </div> :
                null}

                    <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 py-5">
                      <Button
                    type="button"
                    variant="outline"
                    disabled={submitting}
                    className="h-11 px-6 rounded-xl border-error/40 text-error font-semibold text-sm hover:bg-red-50 bg-white"
                    onClick={() => submitDecision("Rejected")}>

                        {submitting ? "Submitting..." : "Reject"}
                      </Button>
                      <Button
                    type="button"
                    disabled={submitting}
                    className={`${ui.primaryBtn} min-w-[130px]`}
                    onClick={() => submitDecision("Approved")}>

                        {submitting ? "Submitting..." : "Approve"}
                      </Button>
                    </div>
                  </>
              }
              </div>
            </>
          }
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>);

}